
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import CloseButton from './CloseButton';
import { ModalCampaign, ModalCampaignQuestion } from '@/types/modalCampaigns';
import ModalContentRenderer from './ModalContentRenderer';
import ModalFooter from './ModalFooter';

interface ModalDesktopVariantProps {
  campaign: ModalCampaign;
  questions: ModalCampaignQuestion[];
  open: boolean;
  userAnswers: Record<string, string[]>;
  quizSubmitted: boolean;
  quizPassed: boolean;
  score: number;
  attemptNumber: number;
  currentQuestionIndex: number;
  canDismiss: boolean;
  onOpenChange: (open: boolean) => void;
  onDismiss: (permanently?: boolean) => void;
  onAnswer: (questionId: string, selectedOptions: string[]) => void;
  onSubmit: () => void;
  onReset: () => void;
  onPrevQuestion: () => void;
  onNextQuestion: () => void;
}

const ModalDesktopVariant: React.FC<ModalDesktopVariantProps> = ({
  campaign,
  questions,
  open,
  userAnswers,
  quizSubmitted,
  quizPassed,
  score,
  attemptNumber,
  currentQuestionIndex,
  canDismiss,
  onOpenChange,
  onDismiss,
  onAnswer,
  onSubmit,
  onReset,
  onPrevQuestion,
  onNextQuestion
}) => {
  const [dismissPermanently, setDismissPermanently] = React.useState(false);
  
  const handleDismiss = () => {
    if (canDismiss) {
      onDismiss(dismissPermanently);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="sm:max-w-[600px] max-h-[90vh] flex flex-col" 
        onPointerDownOutside={e => {
          // Prevent closing when clicking outside if dismissal is not allowed
          if (!canDismiss) {
            e.preventDefault();
          }
        }} 
        onEscapeKeyDown={e => {
          // Prevent closing with ESC key if dismissal is not allowed
          if (!canDismiss) {
            e.preventDefault();
          }
        }}
        // Override the default close button with our custom one
        closeButton={false}
      >
        <CloseButton 
          canDismiss={canDismiss} 
          onDismiss={handleDismiss}
          isMobile={false}
        />
        
        <DialogHeader>
          <DialogTitle>{campaign.title}</DialogTitle>
          {campaign.has_quiz && <DialogDescription>Deze Nieuws+ bevat een quiz</DialogDescription>}
        </DialogHeader>
        
        <div className="flex-grow overflow-y-auto pr-2">
          <ModalContentRenderer
            content={campaign.content}
            hasQuiz={campaign.has_quiz}
            questions={questions}
            userAnswers={userAnswers}
            quizSubmitted={quizSubmitted}
            quizPassed={quizPassed}
            score={score}
            minScore={campaign.min_score !== null ? campaign.min_score : questions.reduce((sum, q) => sum + q.points, 0)}
            attemptNumber={attemptNumber}
            maxAttempts={campaign.max_attempts}
            attemptsExhausted={campaign.max_attempts !== null && attemptNumber >= campaign.max_attempts}
            currentQuestionIndex={currentQuestionIndex}
            onAnswer={onAnswer}
            onSubmit={onSubmit}
            onReset={onReset}
            onPrevQuestion={onPrevQuestion}
            onNextQuestion={onNextQuestion}
          />
        </div>
        
        <DialogFooter className="border-t mt-4 pt-4">
          <ModalFooter
            campaign={campaign}
            canDismiss={canDismiss}
            dismissPermanently={dismissPermanently}
            onDismissPermanentlyChange={setDismissPermanently}
            onDismiss={handleDismiss}
            isMobile={false}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ModalDesktopVariant;
